import type { Extension } from "@medplum/fhirtypes";
import type { IntegerDeclaration, QuantityDeclaration } from "@repo/domain";
import { extensionUrl } from "@repo/fhir-systems";

/**
 * The read-side twin of the engine's `catalog-value` extension (ADR-0021).
 *
 * Views know FHIR meaning on purpose, and they never import codecs — so the
 * shared parameter-value storage is decoded here as well as there. The engine's
 * decoder throws `PersistenceMappingError`, a defect class that means "the
 * server wrote a row it cannot read back". That is the right answer inside a
 * request and the wrong one inside a live query, where it takes the page down.
 * This one reports failure by returning `undefined` and lets the caller decide.
 *
 * Shared by every catalog definition that stores parameter values: device,
 * diagnostic, treatment and protocol. One twin, not four.
 */

export const CATALOG_VALUE_URL = extensionUrl("catalog-value");
/** Must track the engine's constant; a row at any other version is unreadable here. */
export const CATALOG_VALUE_SCHEMA_VERSION = 2;

export type CatalogValueDto =
  | QuantityDeclaration
  | IntegerDeclaration
  | {
      kind: "Choice";
      options: readonly { id: string; label: string }[];
      cardinality: "Single" | "Multiple";
      allowFreeText: boolean;
    }
  | { kind: "Boolean" }
  | { kind: "Text"; maxLength?: number };

function child(parent: Extension, url: string): Extension | undefined {
  return parent.extension?.find((candidate) => candidate.url === url);
}

function children(parent: Extension, url: string): Extension[] {
  return (parent.extension ?? []).filter((candidate) => candidate.url === url);
}

function unit(parent: Extension): { code: string; display: string } | undefined {
  const field = child(parent, "unit");
  if (!field) return undefined;
  const code = child(field, "code")?.valueString;
  const display = child(field, "display")?.valueString;
  return code === undefined || display === undefined ? undefined : { code, display };
}

function options(parent: Extension): readonly { id: string; label: string }[] | undefined {
  const decoded: { id: string; label: string }[] = [];
  for (const option of children(parent, "option")) {
    const id = child(option, "memberId")?.valueString;
    const label = child(option, "label")?.valueString;
    if (id === undefined || label === undefined) return undefined;
    decoded.push({ id, label });
  }
  return decoded;
}

/** `undefined` when the extension is absent, at another schema version, or malformed. */
export function decodeCatalogValue(extension: Extension | undefined): CatalogValueDto | undefined {
  if (!extension || extension.url !== CATALOG_VALUE_URL) return undefined;
  if (child(extension, "schemaVersion")?.valueInteger !== CATALOG_VALUE_SCHEMA_VERSION) return undefined;

  const min = child(extension, "min");
  const max = child(extension, "max");
  switch (child(extension, "kind")?.valueCode) {
    case "Quantity": {
      const measure = unit(extension);
      const step = child(extension, "step")?.valueDecimal;
      return {
        kind: "Quantity",
        ...(measure === undefined ? {} : { unit: measure }),
        ...(min?.valueDecimal === undefined ? {} : { min: min.valueDecimal }),
        ...(max?.valueDecimal === undefined ? {} : { max: max.valueDecimal }),
        ...(step === undefined ? {} : { step }),
      };
    }
    case "Integer": {
      const step = child(extension, "step")?.valueInteger;
      return {
        kind: "Integer",
        ...(min?.valueInteger === undefined ? {} : { min: min.valueInteger }),
        ...(max?.valueInteger === undefined ? {} : { max: max.valueInteger }),
        ...(step === undefined ? {} : { step }),
      };
    }
    case "Choice": {
      const cardinality = child(extension, "cardinality")?.valueCode;
      const allowFreeText = child(extension, "allowFreeText")?.valueBoolean;
      const decoded = options(extension);
      if ((cardinality !== "Single" && cardinality !== "Multiple") || allowFreeText === undefined || !decoded) {
        return undefined;
      }
      return { kind: "Choice", cardinality, allowFreeText, options: decoded };
    }
    case "Boolean":
      return { kind: "Boolean" };
    case "Text": {
      const maxLength = child(extension, "maxLength")?.valueInteger;
      return { kind: "Text", ...(maxLength === undefined ? {} : { maxLength }) };
    }
    default:
      return undefined;
  }
}

/**
 * The inverse, for optimistic rows only. An optimistic row is read back through
 * the same projection a synced row takes, so it has to be written in the same
 * storage shape — any difference reads as a flicker when the server row lands.
 */
export function encodeCatalogValue(value: CatalogValueDto): Extension {
  const parts: Extension[] = [
    { url: "schemaVersion", valueInteger: CATALOG_VALUE_SCHEMA_VERSION },
    { url: "kind", valueCode: value.kind },
  ];

  switch (value.kind) {
    case "Quantity":
      if (value.unit !== undefined) {
        parts.push({
          url: "unit",
          extension: [
            { url: "code", valueString: value.unit.code },
            { url: "display", valueString: value.unit.display },
          ],
        });
      }
      if (value.min !== undefined) parts.push({ url: "min", valueDecimal: value.min });
      if (value.max !== undefined) parts.push({ url: "max", valueDecimal: value.max });
      if (value.step !== undefined) parts.push({ url: "step", valueDecimal: value.step });
      break;
    case "Integer":
      if (value.min !== undefined) parts.push({ url: "min", valueInteger: value.min });
      if (value.max !== undefined) parts.push({ url: "max", valueInteger: value.max });
      if (value.step !== undefined) parts.push({ url: "step", valueInteger: value.step });
      break;
    case "Choice":
      parts.push(
        { url: "cardinality", valueCode: value.cardinality },
        { url: "allowFreeText", valueBoolean: value.allowFreeText },
        ...value.options.map(
          (option): Extension => ({
            url: "option",
            extension: [
              { url: "memberId", valueString: option.id },
              { url: "label", valueString: option.label },
            ],
          }),
        ),
      );
      break;
    case "Boolean":
      break;
    case "Text":
      if (value.maxLength !== undefined) parts.push({ url: "maxLength", valueInteger: value.maxLength });
      break;
  }

  return { url: CATALOG_VALUE_URL, extension: parts };
}
